import UIComponent from "sap/ui/core/UIComponent";
import Device from "sap/ui/Device";
import ApiService from "./services/ApiService";
import JSONModel from "sap/ui/model/json/JSONModel";
import { AppSettings } from "./constants/AppSettings";
import Localization from "sap/base/i18n/Localization";
import { GetCurrentLanguage } from "./utils/LanguageUtil";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import BusyIndicator from "sap/ui/core/BusyIndicator";
import Log from "sap/base/Log";
import { IsUserAuthenticated } from "./services/AuthService";
import LoadContractTypeStyle from "./utils/ContractTypeStyleUtil";

Log.setLevel(Log.Level.WARNING);

/**
 * @namespace akademika.akamont
 */
export default class Component extends UIComponent {
  public static metadata = {
    interfaces: ["sap.ui.core.IAsyncContentCreation"],
    manifest: "json",
  };

  private contentDensityClass: string;
  public apiService: ApiService;
  /** used to display global loading indicator */
  public globalLoadingModel: JSONModel;
  private loadingCounter = 0;
  private _userAuthCheckPromise?: Promise<boolean>;

  public init(): void {
    // call the base component's init function
    super.init();

    // init function must be void to avoid typescript errors, so we have a separate async function to call here
    this.initAsync();
  }

  public async initAsync(): Promise<void> {
    // create the views based on the url/hash
    await this.initApiService();
    this.initDynamicTranslations();
    this.globalLoadingModel = new JSONModel({ isLoading: false });

    const storedLangCode = GetCurrentLanguage();
    if (storedLangCode) Localization.setLanguage(storedLangCode);
    this.getRouter().initialize();

    await LoadContractTypeStyle(this);
  }

  public async initApiService(): Promise<void> {
    this.apiService = await ApiService.Create(
      this.getRouter(),
      this.getModel("i18n") as ResourceModel,
      (value: boolean) => {
        this.handleLoading(value);
      },
      this.checkUserAuthenticationSynchronized.bind(this)
    );
  }

  public initDynamicTranslations(): void {
    const langCodes = AppSettings.languages.map((l) => l.code);
    const dynamicI18nModel = new ResourceModel({
      bundleUrl:
        // AppSettings.apiBaseUrl + "api/Translation/i18n_dynamic.properties",
        this.apiService.getBaseUrl() +
        "api/Translation/i18n_dynamic.properties",
      async: false, // we have a small issue here. UI5 says that "Usage of synchronous loading is deprecated." But if we set async: true, then dynamic translations will not be refreshed correctly when language is changed by the user.
      fallbackLocale: AppSettings.defaultLanguage,
      supportedLocales: langCodes,
    });

    this.setModel(dynamicI18nModel, "i18n_dynamic");
  }

  /**
   * This method can be called to determine whether the sapUiSizeCompact or sapUiSizeCozy
   * design mode class should be set, which influences the size appearance of some controls.
   *
   * @public
   * @return {string} css class, either 'sapUiSizeCompact' or 'sapUiSizeCozy' - or an empty string if no css class should be set
   */
  public getContentDensityClass(): string {
    if (this.contentDensityClass === undefined) {
      // check whether FLP has already set the content density class; do nothing in this case
      if (
        document.body.classList.contains("sapUiSizeCozy") ||
        document.body.classList.contains("sapUiSizeCompact")
      ) {
        this.contentDensityClass = "";
      } else if (!Device.support.touch) {
        // apply "compact" mode if touch is not supported
        this.contentDensityClass = "sapUiSizeCompact";
      } else {
        // "cozy" in case of touch support; default for most sap.m controls, but needed for desktop-first controls like sap.ui.table.Table
        this.contentDensityClass = "sapUiSizeCozy";
      }
    }
    return this.contentDensityClass;
  }

  private decrementLoadingCounter(): void {
    this.loadingCounter--;
    if (this.loadingCounter === 0) {
      this.globalLoadingModel.setProperty("/isLoading", false);
      BusyIndicator.hide();
    }
  }

  public incrementLoadingCounter(): void {
    if (this.loadingCounter === 0) {
      this.globalLoadingModel.setProperty("/isLoading", true);
      BusyIndicator.show(100);
    }

    this.loadingCounter++;
  }

  private handleLoading(value: boolean): void {
    if (value) {
      this.incrementLoadingCounter();
    } else {
      this.decrementLoadingCounter();
    }
  }

  public async whileBusy<T>(fn: () => Promise<T>): Promise<T> {
    this.incrementLoadingCounter();
    try {
      return await fn();
    } finally {
      this.decrementLoadingCounter();
    }
  }

  /**
   * This function prevents multiple token refresh requests to be sent at the same time
   */
  public async checkUserAuthenticationSynchronized(): Promise<boolean> {
    if (!this._userAuthCheckPromise) {
      this._userAuthCheckPromise = IsUserAuthenticated();
    }

    const isAuthenticated = await this._userAuthCheckPromise;
    this._userAuthCheckPromise = undefined;
    return isAuthenticated;
  }
}
